import type { ReactNode } from 'react';

import type { IconComponent } from '@/shared/registry/types';
import { cn } from '@/shared/lib/cn';

export interface EmptyStateProps {
  icon: IconComponent;
  title: ReactNode;
  description?: ReactNode;
  /** Primary / secondary actions. */
  action?: ReactNode;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

/**
 * Empty / zero-data state. The "illustration" is composed from the icon: a tinted tile floating over two
 * offset cards, so every module gets a consistent, lightweight visual without image assets.
 */
export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
  size = 'md',
  className,
}: EmptyStateProps) {
  return (
    <div
      data-slot="empty-state"
      className={cn(
        'flex flex-col items-center justify-center text-center',
        size === 'sm' && 'gap-3 px-4 py-8',
        size === 'md' && 'gap-4 px-6 py-14',
        size === 'lg' && 'gap-5 px-6 py-20',
        className,
      )}
    >
      <div className={cn('relative', size === 'sm' ? 'h-14 w-16' : 'h-20 w-24')} aria-hidden>
        <div className="absolute inset-x-2 top-1 bottom-1 -rotate-6 rounded-xl border bg-card shadow-xs" />
        <div className="absolute inset-x-2 top-1 bottom-1 rotate-6 rounded-xl border bg-card shadow-xs" />
        <div className="absolute inset-x-2 top-0 bottom-2 flex items-center justify-center rounded-xl border bg-gradient-to-b from-card to-muted shadow-sm">
          <div
            className={cn(
              'flex items-center justify-center rounded-lg bg-brand-soft text-brand-text',
              size === 'sm' ? 'size-7' : 'size-10',
            )}
          >
            <Icon className={size === 'sm' ? 'size-4' : 'size-5'} strokeWidth={1.75} />
          </div>
        </div>
      </div>
      <div className="max-w-md space-y-1.5">
        <h3
          className={cn(
            'font-semibold tracking-[-0.01em] text-foreground',
            size === 'sm' ? 'text-sm' : 'text-base',
          )}
        >
          {title}
        </h3>
        {description && <p className="text-sm text-balance text-muted-foreground">{description}</p>}
      </div>
      {action && <div className="flex flex-wrap items-center justify-center gap-2">{action}</div>}
    </div>
  );
}
